import { SkillCategory, SKILL_CATEGORY_COLORS, SKILL_CATEGORY_COLORS_DARK } from '../../../../../db/skillCategories';

export interface SkillWithCategory {
  name: string;
  weight: number;
  category: SkillCategory;
}

export interface SkillItem {
  name: string;
  weight: number;
}

export interface CategorizedSkills {
  profession: SkillWithCategory[];
  lead: SkillWithCategory[];  
  language: SkillWithCategory[];
  tools: SkillWithCategory[];
}

export interface CategorizedSkillItems {
  profession: SkillItem[];
  lead: SkillItem[];
  language: SkillItem[];
  tools: SkillItem[];
}

/**
 * Get CSS classes for skill category styling
 * Automatically detects dark mode from the document
 */
export function getSkillCategoryStyles(category: SkillCategory, isDark?: boolean): string {
  // Auto-detect dark mode if not explicitly provided
  const useDarkMode = isDark !== undefined ? isDark : 
    (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  
  if (useDarkMode) {
    return SKILL_CATEGORY_COLORS_DARK[category] || SKILL_CATEGORY_COLORS_DARK.profession;
  }
  return SKILL_CATEGORY_COLORS[category] || SKILL_CATEGORY_COLORS.profession;
}

/**
 * Get human-readable category name
 */
export function getCategoryDisplayName(category: SkillCategory): string {
  const displayNames: Record<string, string> = {
    profession: 'Professional',
    lead: 'Leadership', 
    language: 'Languages',
    tools: 'Tools'
  };
  return displayNames[category] || category;
}

/**
 * Group skills by category
 */
export function categorizeSkills(skills: SkillWithCategory[]): CategorizedSkills {
  return {
    profession: skills.filter(s => s.category === 'profession'),
    lead: skills.filter(s => s.category === 'lead'),
    language: skills.filter(s => s.category === 'language'),
    tools: skills.filter(s => s.category === 'tools'),
  };
}

/**
 * Get all categories with non-empty skills
 */
export function getActiveCategories(skills: SkillWithCategory[]): SkillCategory[] {
  const categorized = categorizeSkills(skills);
  return Object.entries(categorized)
    .filter(([_, skillsInCategory]) => skillsInCategory.length > 0)
    .map(([category]) => category as SkillCategory);
}

/**
 * Sort skills within each category by weight/percentage (descending) first, then alphabetically
 */
export function sortSkillsByWeight(categorizedSkills: CategorizedSkills): CategorizedSkills {
  const sortSkills = (skills: SkillWithCategory[]) => {
    return skills.sort((a, b) => {
      // First sort by weight (descending)
      const weightDiff = b.weight - a.weight;
      if (weightDiff !== 0) {
        return weightDiff;
      }
      // If weights are equal, sort alphabetically (ascending)
      return a.name.localeCompare(b.name);
    });
  };

  return {
    profession: sortSkills([...categorizedSkills.profession]),
    lead: sortSkills([...categorizedSkills.lead]),
    language: sortSkills([...categorizedSkills.language]),
    tools: sortSkills([...categorizedSkills.tools]),
  };
}

/**
 * Sort skill items within each category by weight/percentage (descending) first, then alphabetically
 */
export function sortSkillItemsByWeight(categorizedSkills: CategorizedSkillItems): CategorizedSkillItems {
  const sortSkills = (skills: SkillItem[]) => {
    return skills.sort((a, b) => {
      // First sort by weight (descending)
      const weightDiff = b.weight - a.weight;
      if (weightDiff !== 0) {
        return weightDiff;
      }
      // If weights are equal, sort alphabetically (ascending)
      return a.name.localeCompare(b.name);
    });
  };

  return {
    profession: sortSkills([...categorizedSkills.profession]),
    lead: sortSkills([...categorizedSkills.lead]),
    language: sortSkills([...categorizedSkills.language]),
    tools: sortSkills([...categorizedSkills.tools]),
  };
}

/**
 * Filter skills by category
 */
export function filterSkillsByCategory(
  skills: SkillWithCategory[], 
  category: SkillCategory
): SkillWithCategory[] {
  return skills.filter(skill => skill.category === category);
}

/**
 * Get the top N skills from each category
 */
export function getTopSkillsByCategory(
  skills: SkillWithCategory[], 
  topN = 5
): CategorizedSkills {
  const categorized = sortSkillsByWeight(categorizeSkills(skills));
  
  return {
    profession: categorized.profession.slice(0, topN),
    lead: categorized.lead.slice(0, topN),
    language: categorized.language.slice(0, topN),
    tools: categorized.tools.slice(0, topN),
  };
}
